// * Import 3rd party lib
import { useEffect } from "react";
import { styled } from "@mui/material/styles";
import Snackbar from "@mui/material/Snackbar";
import { useSelector, useDispatch } from "react-redux";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";

// * Import Kit
import Box from "../../kit/Box";

// * Import custom components
import Header from "../Header/Header";
// import Icon from "../../components/Icon";
import Sidenav from "../../components/SideNav/SideNav";

// * Import utils
import { updateAlertInfo } from "../../redux/slice/userSlice";
import { useTheme } from "@mui/styles";

// * Define Custom Styles
const MuiAlert = styled(Alert)(({ theme }) => ({
  height: theme.size[30],
  alignItems: "center",
  "& .MuiAlert-message": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  "& .MuiAlert-icon": {
    color: theme.palette.button.text,
  },
}));

// * Define required styled components
const Root = styled(Box)(() => ({
  height: "100vh",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
}));

const SidebarWrapper = styled(Box)(({ theme }) => ({
  height: "100vh",
  flexShrink: 0,
  zIndex: 1000,
  borderRight: "none", // remove divider line; rely on sidenav shadow
  transition: 'width 0.3s ease',
}));

const OverlaySidebarWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  height: "100vh",
  zIndex: 9999,
  borderRight: "none", // remove divider line; rely on sidenav shadow
  transition: 'width 0.3s ease',
}));

const RightContentWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  backgroundColor: theme.palette.primary.backgroundColor,
  minWidth: 0, // Allows flex child to shrink below its minimum content size
  overflow: "visible", // allow header shadow to render
}));

const HeaderWrapper = styled(Box)(({ theme }) => ({
  height: theme.size[50],
  flexShrink: 0,
  transition: `all ${theme.transitions.duration.short}ms`,
  boxShadow: "0px 6px 6px 0px #0000000F",
  backgroundColor: theme.palette.header.main,
  position: "relative",
  zIndex: 2,
  borderBottom: `${theme.size[1]} solid ${theme.palette.tertiary.lightGrey}`,
}));

const BodyWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  transition: `all ${theme.transitions.duration.short}ms ease-in-out`,
  backgroundColor: theme.palette.primary.backgroundColor,
  overflow: "auto",
}));

const MainWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: "auto",
  backgroundColor: theme.palette.grey["0"],
  paddingTop: theme.size[40],
  paddingBottom: theme.size[25],
  padding: "0px",
  height: "calc(100vh - 55px)",
  width: "100%",
  transition: `all ${theme.transitions.duration.short}ms ease-in-out`,
}));

function LayoutTopSideBottom(props = {}) {
  const snackbar = useSelector((state) => state.user.alertInfo);
  const isCollapsed = useSelector((state) => state.selections.isSidebarCollapsed);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (window.location.pathname === "/") {
      navigate("/home");
    }
    // eslint-disable-next-line
  }, [window.location.pathname]);

  // * HANDLERS
  const handleCloseSnackbar = () => {
    dispatch(
      updateAlertInfo({
        message: "",
        open: false,
      })
    );
  };

  return (
    <>
      <Root>
        <SidebarWrapper component="aside">
          <Sidenav />
        </SidebarWrapper>
        {/* Overlay sidebar when expanded */}
        {!isCollapsed && (
          <OverlaySidebarWrapper component="aside">
            <Sidenav />
          </OverlaySidebarWrapper>
        )}
        <RightContentWrapper>
          <HeaderWrapper component="section">
            <Header />
          </HeaderWrapper>
          <BodyWrapper component="section">
            <MainWrapper
              component="main"
              sx={{ 
                backgroundColor: theme.palette.primary.backgroundColor,
                ...(location.pathname === "/" || location.pathname === "/home" ? {
                  width: "100%",
                  padding: 0
                } : {})
              }}
            >
              <Outlet key={location.pathname} />
            </MainWrapper>
          </BodyWrapper>
        </RightContentWrapper>
      </Root>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => handleCloseSnackbar()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          severity={snackbar.severity || "info"}
          style={{
            color: theme.palette.banner.color,
            backgroundColor: theme.palette.banner.backgroundColor,
            padding: theme.size[20],
          }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default LayoutTopSideBottom;
